import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/system";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { authAPI } from "../../services/api";
import CustomButton from "../ui/CustomButton";

const BackgroundBox = styled(Box)(({ theme }) => ({
  minHeight: "90vh",
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "center",
  padding: theme.spacing(2),
  paddingTop: "8vh",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(1.5),
    backgroundColor: "transparent",
    "& .MuiOutlinedInput-input": {
      backgroundColor: "transparent",
    },
  },
  "& .MuiInputLabel-root": {
    backgroundColor: "transparent",
  },
}));

function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { value } = e.target;
    setEmail(value);

    // Clear field error when user starts typing
    if (fieldErrors.email) {
      setFieldErrors((prev) => ({
        ...prev,
        email: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setFieldErrors({});

    // Validation
    const errors = {};

    if (!email.trim()) {
      errors.email = "Email is required";
    } else {
      // Email validation
      const emailRegex =
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
      if (!emailRegex.test(email)) {
        errors.email = "Please enter a valid email address";
      }
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    try {
      await authAPI.forgotPassword(email);
      setSuccess(true);
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        "Failed to send reset email. Please try again later.";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <BackgroundBox>
        <Box
          sx={{
            padding: 4,
            width: "100%",
            maxWidth: 400,
            borderRadius: 4,
            textAlign: "center",
            backgroundColor: "transparent",
          }}
        >
          <Typography variant="h5" fontWeight="500" sx={{ mb: "2rem" }}>
            Check Your Email
          </Typography>

          <Alert
            severity="success"
            sx={{ mb: 3, borderRadius: 2, textAlign: "left" }}
          >
            If an account with that email exists, we've sent you a password
            reset link. Please check your email and follow the instructions.
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Didn't receive the email? Check your spam folder or try again.
          </Typography>

          <CustomButton
            fullWidth
            variant="outlined"
            onClick={() => {
              setSuccess(false);
              setEmail("");
            }}
            sx={{
              height: "50px",
              borderRadius: "25px",
              textTransform: "none",
              mt: 2,
            }}
          >
            Send Another Email
          </CustomButton>

          <Box
            onClick={() => navigate("/login")}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              mt: 3,
              color: "text.secondary",
              "&:hover": {
                color: "black",
              },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 16, mr: 1 }} />
            <Typography variant="body2">Back to Login</Typography>
          </Box>
        </Box>
      </BackgroundBox>
    );
  }

  return (
    <BackgroundBox>
      <Box
        sx={{
          padding: 4,
          width: "100%",
          maxWidth: 400,
          borderRadius: 4,
          textAlign: "center",
          backgroundColor: "transparent",
        }}
      >
        <Typography variant="h5" fontWeight="500" sx={{ mb: "2rem" }}>
          Forgot Password?
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 3, textAlign: "left" }}
        >
          Enter your email address and we'll send you a link to reset your
          password.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <StyledTextField
            type="email"
            value={email}
            onChange={handleInputChange}
            fullWidth
            autoFocus
            placeholder="Email"
            error={!!fieldErrors.email}
            helperText={fieldErrors.email}
          />

          <CustomButton
            fullWidth
            variant="contained"
            type="submit"
            size="large"
            disabled={loading}
            preventSpam={true}
            spamDelay={2000}
            sx={{
              height: "50px",
              borderRadius: "25px",
              textTransform: "none",
              mt: 2,
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Send Reset Link"
            )}
          </CustomButton>

          <Box
            onClick={() => navigate("/login")}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              mt: 3,
              color: "text.secondary",
              "&:hover": {
                color: "black",
              },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 16, mr: 1 }} />
            <Typography variant="body2">Back to Login</Typography>
          </Box>
        </form>
      </Box>
    </BackgroundBox>
  );
}

export default ForgotPassword;
